import { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '@/components/common/Layout/Layout';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/lib/store';
import { ROUTES } from '@/constants/routes';
import api, { showErrorToast } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Target,
  Users,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Save,
  ArrowRightLeft,
  CheckCircle,
  Sparkles,
  Filter,
  Search,
  X,
  Percent,
  Plus,
  Minus,
  Receipt,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ComboboxSelect, useComboboxOptions } from '@/components/ui/combobox-select';
import { MatterCombobox, UserCombobox } from '@/components/common/EntityCombobox';
import { DatePicker } from '@/components/ui/date-picker';
import { decimalToHHMM, hhmmToDecimal } from '@/utils/time-format';
import { isSuperUser as checkSuperUser, isAdmin as checkAdmin } from '@/lib/permissions';
import { AdjustmentLinesEditor } from '@/components/invoices/unified-invoicing/AdjustmentLinesEditor';
import { WriteOffDialog } from '@/components/timesheet';
import type { AdjustmentLine } from '@/types/invoicing';
import { formatCurrency } from '@/utils/document';

const formatDateDisplay = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    // Handle both 'YYYY-MM-DD' and 'YYYY-MM-DDT...' formats
    const datePart = dateStr.substring(0, 10);
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return dateStr;
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  } catch {
    return dateStr;
  }
};

/** Parse DD/MM/YYYY → YYYY-MM-DD. Returns null if invalid. */
const parseDateInput = (value: string): string | null => {
  if (!value) return null;
  const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const day = m[1].padStart(2, '0');
  const month = m[2].padStart(2, '0');
  const year = m[3];
  const d = parseInt(day, 10);
  const mo = parseInt(month, 10);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return `${year}-${month}-${day}`;
};

const formatRate = (rate: number | string | null | undefined) => {
  if (!rate && rate !== 0) return '';
  const numRate = typeof rate === 'string' ? parseFloat(rate) : rate;
  if (isNaN(numRate)) return '';
  return numRate % 1 === 0 ? numRate.toString() : numRate.toFixed(2);
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface TimesheetEntry {
  id: number;
  matter: number;
  user: number;
  description: string;
  hours: number;
  billable: boolean;
  billable_hours: number;
  fee: number;
  rate: number | null;
  net_rate: number | null;
  fixed_fee: number | null;
  date: string;
  reviewed: boolean;
  locked: boolean;
  invoiced: boolean;
  timer_active: boolean;
  created_at: string;
  updated_at: string;
}

interface LocalEntry extends TimesheetEntry {
  // Local edits (overrides for display/computation)
  local_billable_hours?: string; // HH:MM string for display
  local_hours?: string; // HH:MM string for hours display
  local_rate?: string;
  local_description?: string;
  local_date?: string; // DD/MM/YYYY for display
  dirty?: boolean;
}

interface UserSummary {
  userId: number;
  userName: string;
  totalBillableHours: number;
  originalBillableHours: number;
  totalFee: number;
  avgRate: number;
  entryCount: number;
}

// ─── Invoice Adjustments ─────────────────────────────────────────────────────

interface InvoiceAdjustments {
  discount_type: 'none' | 'percentage' | 'fixed' | 'both';
  discount_percentage: string;
  discount_amount: string;
  discount_description: string;
  show_discount_on_invoice: boolean;
  flat_fee_adjustment: string;
  flat_fee_description: string;
  show_flat_fee_on_invoice: boolean;
  hour_adjustment: string;
  rate_adjustment: string;
  hour_adjustment_description: string;
  show_hour_adjustment_on_invoice: boolean;
  target_amount: string;
  fixed_deduction_amount: string;
  fixed_deduction_description: string;
  show_fixed_deduction_on_invoice: boolean;
  detail_level: '' | 'short' | 'summary' | 'detailed';
  adjustment_lines: AdjustmentLine[];
}

const INVOICE_DETAIL_LEVELS = [
  { value: 'short', label: 'Short', description: 'One line per team member' },
  { value: 'summary', label: 'Summary', description: 'Grouped by team member with totals' },
  { value: 'detailed', label: 'Detailed', description: 'Every timesheet entry listed' },
];

const DEFAULT_ADJUSTMENTS: InvoiceAdjustments = {
  discount_type: 'none',
  discount_percentage: '',
  discount_amount: '',
  discount_description: '',
  show_discount_on_invoice: true,
  flat_fee_adjustment: '',
  flat_fee_description: '',
  show_flat_fee_on_invoice: true,
  hour_adjustment: '',
  rate_adjustment: '',
  hour_adjustment_description: '',
  show_hour_adjustment_on_invoice: true,
  target_amount: '',
  fixed_deduction_amount: '',
  fixed_deduction_description: '',
  show_fixed_deduction_on_invoice: true,
  detail_level: '',
  adjustment_lines: [],
};

const DISCOUNT_TYPES = [
  { value: 'none', label: 'No Discount' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed', label: 'Fixed Amount' },
  { value: 'both', label: 'Percentage + Fixed' },
];

/** Convert server response (numeric fields) to the string-based InvoiceAdjustments the UI expects. */
const serverToLocal = (data: any): InvoiceAdjustments => ({
  discount_type: data.discount_type || 'none',
  discount_percentage: data.discount_percentage && Number(data.discount_percentage) !== 0 ? String(data.discount_percentage) : '',
  discount_amount: data.discount_amount && Number(data.discount_amount) !== 0 ? String(data.discount_amount) : '',
  discount_description: data.discount_description || '',
  show_discount_on_invoice: data.show_discount_on_invoice ?? true,
  flat_fee_adjustment: data.flat_fee_adjustment && Number(data.flat_fee_adjustment) !== 0 ? String(data.flat_fee_adjustment) : '',
  flat_fee_description: data.flat_fee_description || '',
  show_flat_fee_on_invoice: data.show_flat_fee_on_invoice ?? true,
  hour_adjustment: data.hour_adjustment && Number(data.hour_adjustment) !== 0 ? String(data.hour_adjustment) : '',
  rate_adjustment: data.rate_adjustment && Number(data.rate_adjustment) !== 0 ? String(data.rate_adjustment) : '',
  hour_adjustment_description: data.hour_adjustment_description || '',
  show_hour_adjustment_on_invoice: data.show_hour_adjustment_on_invoice ?? true,
  target_amount: data.target_amount && Number(data.target_amount) !== 0 ? String(data.target_amount) : '',
  fixed_deduction_amount: data.fixed_deduction_amount && Number(data.fixed_deduction_amount) !== 0 ? String(data.fixed_deduction_amount) : '',
  fixed_deduction_description: data.fixed_deduction_description || '',
  show_fixed_deduction_on_invoice: data.show_fixed_deduction_on_invoice ?? true,
  detail_level: data.detail_level || '',
  adjustment_lines: (data.adjustment_lines || []).map((line: any) => ({
    id: line.id,
    line_type: line.line_type || 'deduction',
    description: line.description || '',
    amount: line.amount != null ? String(line.amount) : '',
    percentage: line.percentage != null ? line.percentage : null,
    show_on_invoice: line.show_on_invoice ?? true,
    order: line.order ?? 0,
  })),
});

/** Convert local string-based adjustments to the numeric payload the server expects. */
const localToServer = (adj: InvoiceAdjustments): Record<string, any> => ({
  discount_type: adj.discount_type,
  discount_percentage: adj.discount_percentage ? parseFloat(adj.discount_percentage) : 0,
  discount_amount: adj.discount_amount ? parseFloat(adj.discount_amount) : 0,
  discount_description: adj.discount_description,
  show_discount_on_invoice: adj.show_discount_on_invoice,
  flat_fee_adjustment: adj.flat_fee_adjustment ? parseFloat(adj.flat_fee_adjustment) : 0,
  flat_fee_description: adj.flat_fee_description,
  show_flat_fee_on_invoice: adj.show_flat_fee_on_invoice,
  hour_adjustment: adj.hour_adjustment ? parseFloat(adj.hour_adjustment) : 0,
  rate_adjustment: adj.rate_adjustment ? parseFloat(adj.rate_adjustment) : 0,
  hour_adjustment_description: adj.hour_adjustment_description,
  show_hour_adjustment_on_invoice: adj.show_hour_adjustment_on_invoice,
  target_amount: adj.target_amount ? parseFloat(adj.target_amount) : null,
  fixed_deduction_amount: adj.fixed_deduction_amount ? parseFloat(adj.fixed_deduction_amount) : 0,
  fixed_deduction_description: adj.fixed_deduction_description,
  show_fixed_deduction_on_invoice: adj.show_fixed_deduction_on_invoice,
  detail_level: adj.detail_level,
  adjustment_lines: adj.adjustment_lines.map((line, i) => ({
    line_type: line.line_type,
    description: line.description,
    amount: line.amount ? parseFloat(String(line.amount)) : 0,
    percentage: line.percentage != null ? parseFloat(String(line.percentage)) : null,
    show_on_invoice: line.show_on_invoice ?? true,
    order: line.order ?? i,
  })),
});

// ─── Memoized table row ──────────────────────────────────────────────────────

interface TimesheetRowProps {
  entry: LocalEntry;
  selected: boolean;
  matterLabel: string;
  userLabel: string;
  matters: any[];
  users: any[];
  onToggleSelect: (id: number) => void;
  onUpdateEntry: (id: number, field: keyof LocalEntry, value: any) => void;
  onUpdateBillableHours: (id: number, value: string) => void;
  onFormatBillableHoursBlur: (id: number, value: string) => void;
  onUpdateRate: (id: number, value: string) => void;
  onFormatRateBlur: (id: number, value: string) => void;
  onUpdateDate: (id: number, displayValue: string) => void;
  onCommitDate: (id: number, displayValue: string) => void;
}

const TimesheetRow = memo(function TimesheetRow({
  entry,
  selected,
  matters,
  users,
  onToggleSelect,
  onUpdateEntry,
  onUpdateBillableHours,
  onFormatBillableHoursBlur,
  onUpdateRate,
  onFormatRateBlur,
  onUpdateDate,
  onCommitDate,
}: TimesheetRowProps) {
  const canEdit = !entry.timer_active && !entry.locked && !entry.invoiced;

  // Compute fee once
  const bh = typeof entry.local_billable_hours === 'string' && entry.local_billable_hours.includes(':')
    ? hhmmToDecimal(entry.local_billable_hours)
    : entry.billable_hours;
  const r = entry.local_rate && entry.local_rate !== '' ? parseFloat(entry.local_rate) : (entry.rate || 0);
  const computedFee = entry.billable ? bh * r : 0;

  return (
    <TableRow
      className={cn(
        entry.dirty && 'bg-yellow-50/60',
        entry.reviewed && 'bg-green-50',
        entry.locked && 'bg-orange-50'
      )}
    >
      <TableCell className="w-10 py-2 px-2">
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelect(entry.id)}
        />
      </TableCell>

      {/* Date – DD/MM/YYYY text input */}
      <TableCell className="w-28 py-2 px-2">
        <Input
          type="text"
          placeholder="DD/MM/YYYY"
          value={entry.local_date ?? formatDateDisplay(entry.date)}
          onChange={(e) => onUpdateDate(entry.id, e.target.value)}
          onBlur={(e) => onCommitDate(entry.id, e.target.value)}
          className="h-7 text-xs w-full"
          disabled={!canEdit}
        />
      </TableCell>

      {/* Matter – editable searchable combobox */}
      <TableCell className="w-32 py-2 px-2">
        <MatterCombobox
          value={entry.matter ?? null}
          onChange={(id) => id && onUpdateEntry(entry.id, 'matter', id)}
          matters={matters}
          includeInactive
          disabled={!canEdit}
          triggerClassName="h-7 text-xs px-1"
        />
      </TableCell>

      {/* User – editable searchable combobox */}
      <TableCell className="w-28 py-2 px-2">
        <UserCombobox
          value={entry.user ?? null}
          onChange={(id) => id && onUpdateEntry(entry.id, 'user', id)}
          users={users}
          disabled={!canEdit}
          triggerClassName="h-7 text-xs px-1"
        />
      </TableCell>

      {/* Description */}
      <TableCell className="w-48 py-2 px-2">
        <Input
          value={entry.local_description ?? entry.description}
          onChange={(e) => onUpdateEntry(entry.id, 'description', e.target.value)}
          placeholder="Description"
          className="h-7 text-xs w-full"
          disabled={!canEdit}
        />
      </TableCell>

      {/* Billable Hours */}
      <TableCell className="w-20 py-2 px-2">
        <Input
          value={entry.local_billable_hours || ''}
          onChange={(e) => onUpdateBillableHours(entry.id, e.target.value)}
          onBlur={(e) => onFormatBillableHoursBlur(entry.id, e.target.value)}
          placeholder="0"
          className={cn(
            'h-7 text-xs text-center',
            !entry.billable && 'bg-muted text-muted-foreground'
          )}
          disabled={!canEdit || !entry.billable}
        />
      </TableCell>

      {/* Rate */}
      <TableCell className="w-20 py-2 px-2">
        <Input
          value={entry.local_rate || ''}
          onChange={(e) => onUpdateRate(entry.id, e.target.value)}
          onBlur={(e) => onFormatRateBlur(entry.id, e.target.value)}
          placeholder="Rate"
          className="h-7 text-xs text-center [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          style={{ MozAppearance: 'textfield' }}
          disabled={!canEdit}
        />
      </TableCell>

      {/* Fee */}
      <TableCell className="w-20 py-2 px-2 text-right">
        <span className={cn('text-sm', entry.dirty && 'text-orange-600 font-medium')}>
          {formatCurrency(computedFee)}
        </span>
      </TableCell>

      {/* Status */}
      <TableCell className="w-12 py-2 px-2">
        <div className="flex gap-0.5">
          {entry.reviewed && <CheckCircle className="h-3.5 w-3.5 text-green-500" />}
        </div>
      </TableCell>
    </TableRow>
  );
});

// ─── Component ───────────────────────────────────────────────────────────────

export function TimesheetAdminReview() {
  const { user } = useAuth();
  const userGroups = user?.groups || [];
  const { matters, users, clients, fetchAll, isLoading: storeLoading } = useStore();
  const [searchParams] = useSearchParams();

  // Auth checks
  const isSuperUser = checkSuperUser(userGroups);
  const isAdmin = checkAdmin(userGroups);
  const isUserAdmin = isSuperUser || isAdmin;

  // Matter filter from URL
  const matterIdParam = searchParams.get('matter');
  const matterId = matterIdParam ? parseInt(matterIdParam, 10) : null;

  // Date filters from URL (passed by invoice creation page)
  const dateFromParam = searchParams.get('date_from') || '';
  const dateToParam = searchParams.get('date_to') || '';

  // State
  const [entries, setEntries] = useState<LocalEntry[]>([]);
  const [originalEntries, setOriginalEntries] = useState<TimesheetEntry[]>([]);
  const [isLoading, setIsLoading] = useState(!!matterId);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [transferMatterOpen, setTransferMatterOpen] = useState(false);
  const [transferMatterId, setTransferMatterId] = useState<number | null>(null);
  const [memberSummaryOpen, setMemberSummaryOpen] = useState(true);

  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState<string>(dateFromParam);
  const [filterDateTo, setFilterDateTo] = useState<string>(dateToParam);
  const [filterUserIds, setFilterUserIds] = useState<number[]>([]);
  const [filterMatterIds, setFilterMatterIds] = useState<number[]>(matterId ? [matterId] : []);
  const [filterSearch, setFilterSearch] = useState('');

  // Server-side sum (from /api/timesheets-sum/)
  const [serverSum, setServerSum] = useState<{
    hours: number; billable_hours: number; avg_rate: number; total_fee: number;
  } | null>(null);

  // Target amount state
  const [targetAmount, setTargetAmount] = useState('');
  const [targetSliderValue, setTargetSliderValue] = useState(100); // percentage of current total

  // Per-member rate overrides: userId -> new rate string
  const [memberRateOverrides, setMemberRateOverrides] = useState<Record<number, string>>({});

  // Per-user slider values: userId -> percentage (100 = original)
  const [userSliderValues, setUserSliderValues] = useState<Record<number, number>>({});
  // Per-user target amounts: userId -> target fee string
  const [userTargetAmounts, setUserTargetAmounts] = useState<Record<number, string>>({});

  // Invoice adjustments (persisted to server per matter)
  const [adjustments, setAdjustments] = useState<InvoiceAdjustments>({ ...DEFAULT_ADJUSTMENTS });
  const [adjustmentsLoaded, setAdjustmentsLoaded] = useState(false);

  // Load adjustments from server when matter changes
  const activeMatterIdForAdj = filterMatterIds.length > 0 ? filterMatterIds[0] : matterId;
  useEffect(() => {
    if (!activeMatterIdForAdj) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/matters/${activeMatterIdForAdj}/invoice-adjustments/`);
        if (!cancelled) {
          setAdjustments(serverToLocal(res.data));
          setAdjustmentsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setAdjustments({ ...DEFAULT_ADJUSTMENTS });
          setAdjustmentsLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeMatterIdForAdj]);

  // Debounced save adjustments to server whenever they change
  useEffect(() => {
    if (!activeMatterIdForAdj || !adjustmentsLoaded) return;
    const timer = setTimeout(() => {
      api.put(`/api/matters/${activeMatterIdForAdj}/invoice-adjustments/`, localToServer(adjustments))
        .catch(() => { /* silent — user sees the values locally regardless */ });
    }, 600);
    return () => clearTimeout(timer);
  }, [adjustments, activeMatterIdForAdj, adjustmentsLoaded]);

  const updateAdjustment = useCallback(<K extends keyof InvoiceAdjustments>(key: K, value: InvoiceAdjustments[K]) => {
    setAdjustments((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Lookup maps
  const matterMap = useMemo(() => {
    const map = new Map<number, string>();
    matters.forEach((m) => {
      map.set(m.id, m.group_name ? `${m.group_name} - ${m.name}` : m.name);
    });
    return map;
  }, [matters]);

  const userMap = useMemo(() => {
    const map = new Map<number, string>();
    users.forEach((u) => {
      map.set(u.id, `${u.first_name} ${u.last_name}`);
    });
    return map;
  }, [users]);

  // The "active" matter is either the URL param or whatever the user picked in the filter
  const activeMatterId = filterMatterIds.length > 0 ? filterMatterIds[0] : matterId;
  const matterName = activeMatterId ? matterMap.get(activeMatterId) || 'Unknown Matter' : null;

  // Sorted matter options for the combobox (active matters, client names searchable)
  const sortedMatterOptions = useComboboxOptions(
    [...matters].filter(m => m.status === 'active').sort((a, b) => {
      const la = a.group_name ? `${a.group_name} - ${a.name}` : a.name;
      const lb = b.group_name ? `${b.group_name} - ${b.name}` : b.name;
      return la.localeCompare(lb);
    }),
    (m) => m.id.toString(),
    (m) => m.group_name ? `${m.group_name} - ${m.name}` : m.name,
    (m) => {
      const activeConfig = m.fee_sharing_configs?.find((c: any) => c.is_active) || m.fee_sharing_configs?.[0];
      const clientNames = activeConfig?.participants?.map((p: any) => {
        const client = clients.find((c: any) => c.id === p.client);
        return client?.name || '';
      }).join(' ') || '';
      return `${m.name} ${m.group_name || ''} ${clientNames}`;
    }
  );

  // ─── Data fetching ────────────────────────────────────────────────────────

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchEntries = useCallback(async () => {
    // GUARD: never fetch without a matter — would pull the entire system
    const mId = filterMatterIds.length > 0 ? filterMatterIds[0] : matterId;
    if (!mId) return;

    setIsLoading(true);
    try {
      // Build filter params shared between list and sum endpoints
      const filterParams = new URLSearchParams();
      filterParams.append('invoiced', 'false');
      filterParams.append('matter', mId.toString());
      if (filterUserIds.length > 0) filterParams.append('user_id', filterUserIds.join(','));
      if (filterDateFrom) filterParams.append('start_date', filterDateFrom);
      if (filterDateTo) filterParams.append('end_date', filterDateTo);
      if (filterSearch) filterParams.append('search', filterSearch);
      filterParams.append('billable', 'true');

      // Fire sum request in parallel
      const sumPromise = api.get(`${ROUTES.API.TIMESHEET_SUM}?${filterParams.toString()}`);

      // Fetch ALL pages of timesheets
      const listParams = new URLSearchParams(filterParams);
      listParams.append('page_size', '500');
      listParams.append('sort_by', 'user__last_name,date');

      const allResults: any[] = [];
      let nextUrl: string | null = `${ROUTES.API.TIMESHEET}?${listParams.toString()}`;

      while (nextUrl) {
        const response: any = await api.get(nextUrl);
        allResults.push(...response.data.results);
        // DRF returns absolute URLs for pagination (e.g. http://server.sentra.be/api/...).
        // Convert to relative path so axios uses its configured baseURL (HTTPS).
        const rawNext: string | null = response.data.next ?? null;
        nextUrl = rawNext ? rawNext.replace(/^https?:\/\/[^/]+/, '') : null;
      }

      const raw: TimesheetEntry[] = allResults.map((e: any) => ({
        id: e.id,
        matter: e.matter,
        user: e.user,
        description: e.description,
        hours: parseFloat(e.hours) || 0,
        billable: e.billable,
        billable_hours: parseFloat(e.billable_hours?.toString() || '0') || 0,
        fee: parseFloat(e.fee?.toString() || '0') || 0,
        rate: e.rate ? parseFloat(e.rate) : null,
        net_rate: e.net_rate ? parseFloat(e.net_rate) : null,
        fixed_fee: e.fixed_fee ? parseFloat(e.fixed_fee) : null,
        date: e.date,
        reviewed: e.reviewed,
        locked: e.locked,
        invoiced: e.invoiced,
        timer_active: e.timer_active,
        created_at: e.created_at,
        updated_at: e.updated_at,
      }));

      // Await sum response
      try {
        const sumResponse = await sumPromise;
        setServerSum({
          hours: parseFloat(sumResponse.data.hours) || 0,
          billable_hours: parseFloat(sumResponse.data.billable_hours) || 0,
          avg_rate: parseFloat(sumResponse.data.avg_rate) || 0,
          total_fee: parseFloat(sumResponse.data.total_fee) || 0,
        });
      } catch {
        setServerSum(null);
      }

      // Sort by date ascending
      raw.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

      setOriginalEntries(raw);
      setEntries(
        raw.map((e) => ({
          ...e,
          local_billable_hours: decimalToHHMM(e.billable_hours),
          local_hours: decimalToHHMM(e.hours),
          local_rate: e.rate !== null ? formatRate(e.rate) : '',
          local_description: e.description,
          local_date: formatDateDisplay(e.date),
          dirty: false,
        }))
      );
      setSelectedIds(new Set());
      setMemberRateOverrides({});
      setUserSliderValues({});
      setUserTargetAmounts({});
      setTargetAmount('');
      setTargetSliderValue(100);
    } catch (error) {
      showErrorToast(error, 'Error', 'Failed to fetch timesheets');
    } finally {
      setIsLoading(false);
    }
  }, [matterId, filterMatterIds, filterUserIds, filterDateFrom, filterDateTo, filterSearch]);

  useEffect(() => {
    const mId = filterMatterIds.length > 0 ? filterMatterIds[0] : matterId;
    if (!storeLoading && matters.length > 0 && mId) {
      fetchEntries();
    }
  }, [storeLoading, matters.length, fetchEntries]);

  // ─── Computed summaries ───────────────────────────────────────────────────

  const totalFee = useMemo(() => {
    return entries.reduce((sum, e) => {
      if (!e.billable) return sum;
      const bh = typeof e.local_billable_hours === 'string' && e.local_billable_hours.includes(':')
        ? hhmmToDecimal(e.local_billable_hours)
        : e.billable_hours;
      const r = e.local_rate && e.local_rate !== '' ? parseFloat(e.local_rate) : (e.rate || 0);
      return sum + bh * r;
    }, 0);
  }, [entries]);

  const originalTotalFee = useMemo(() => {
    if (serverSum) return serverSum.total_fee;
    return originalEntries.reduce((sum, e) => {
      if (!e.billable) return sum;
      return sum + (e.fee || 0);
    }, 0);
  }, [originalEntries, serverSum]);

  const totalBillableHours = useMemo(() => {
    return entries.reduce((sum, e) => {
      if (!e.billable) return sum;
      const bh = typeof e.local_billable_hours === 'string' && e.local_billable_hours.includes(':')
        ? hhmmToDecimal(e.local_billable_hours)
        : e.billable_hours;
      return sum + bh;
    }, 0);
  }, [entries]);

  const userSummaries = useMemo((): UserSummary[] => {
    const map = new Map<number, UserSummary>();

    entries.forEach((e) => {
      if (!map.has(e.user)) {
        map.set(e.user, {
          userId: e.user,
          userName: userMap.get(e.user) || `User ${e.user}`,
          totalBillableHours: 0,
          originalBillableHours: 0,
          totalFee: 0,
          avgRate: 0,
          entryCount: 0,
        });
      }
      const s = map.get(e.user)!;
      s.entryCount += 1;

      if (e.billable) {
        const bh = typeof e.local_billable_hours === 'string' && e.local_billable_hours.includes(':')
          ? hhmmToDecimal(e.local_billable_hours)
          : e.billable_hours;
        const r = e.local_rate && e.local_rate !== '' ? parseFloat(e.local_rate) : (e.rate || 0);
        s.totalBillableHours += bh;
        s.originalBillableHours += e.billable_hours;
        s.totalFee += bh * r;
      }
    });

    // Compute average rates
    map.forEach((s) => {
      s.avgRate = s.totalBillableHours > 0 ? s.totalFee / s.totalBillableHours : 0;
    });

    return Array.from(map.values()).sort((a, b) => a.userName.localeCompare(b.userName));
  }, [entries, userMap]);

  // Original per-user fees for slider calculations
  const originalUserFees = useMemo(() => {
    const map = new Map<number, { fee: number; billableHours: number }>();
    originalEntries.forEach((e) => {
      if (!e.billable) return;
      const current = map.get(e.user) || { fee: 0, billableHours: 0 };
      current.fee += e.fee || (e.billable_hours * (e.rate || 0));
      current.billableHours += e.billable_hours;
      map.set(e.user, current);
    });
    return map;
  }, [originalEntries]);

  // ─── Entry editing ────────────────────────────────────────────────────────

  const updateEntry = useCallback((id: number, field: keyof LocalEntry, value: any) => {
    setEntries((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e;
        const updated: LocalEntry = { ...e, [field]: value, dirty: true };
        // Sync local_description when description changes
        if (field === 'description') updated.local_description = value;
        return updated;
      })
    );
  }, []);

  const updateBillableHoursDisplay = useCallback((id: number, value: string) => {
    setEntries((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e;
        return { ...e, local_billable_hours: value, dirty: true };
      })
    );
  }, []);

  const formatBillableHoursOnBlur = useCallback((id: number, value: string) => {
    if (!value || value === '0') {
      updateEntry(id, 'local_billable_hours', '00:00');
      return;
    }
    let hours: number;
    if (value.includes(':')) {
      hours = hhmmToDecimal(value);
    } else {
      hours = parseFloat(value);
    }
    if (!isNaN(hours)) {
      setEntries((prev) =>
        prev.map((e) => {
          if (e.id !== id) return e;
          return { ...e, local_billable_hours: decimalToHHMM(hours), billable_hours: hours, dirty: true };
        })
      );
    }
  }, []);

  const updateRateDisplay = useCallback((id: number, value: string) => {
    setEntries((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e;
        return { ...e, local_rate: value, dirty: true };
      })
    );
  }, []);

  const formatRateOnBlur = useCallback((id: number, value: string) => {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      setEntries((prev) =>
        prev.map((e) => {
          if (e.id !== id) return e;
          return { ...e, local_rate: formatRate(parsed), rate: parsed, dirty: true };
        })
      );
    }
  }, []);

  // Date editing callbacks (used by memoized row)
  const handleDateChange = useCallback((id: number, displayValue: string) => {
    setEntries((prev) =>
      prev.map((en) =>
        en.id === id ? { ...en, local_date: displayValue, dirty: true } : en
      )
    );
  }, []);

  const handleDateCommit = useCallback((id: number, displayValue: string) => {
    const parsed = parseDateInput(displayValue);
    if (parsed) {
      setEntries((prev) =>
        prev.map((en) =>
          en.id === id
            ? { ...en, date: parsed, local_date: formatDateDisplay(parsed), dirty: true }
            : en
        )
      );
    } else if (displayValue) {
      // Revert to last valid date
      setEntries((prev) =>
        prev.map((en) =>
          en.id === id ? { ...en, local_date: formatDateDisplay(en.date) } : en
        )
      );
    }
  }, []);

  // ─── Target Amount Logic ──────────────────────────────────────────────────

  const applyTargetAmount = useCallback(() => {
    const target = parseFloat(targetAmount);
    if (isNaN(target) || target <= 0) return;

    // Current total fee from billable entries
    const currentTotal = entries.reduce((sum, e) => {
      if (!e.billable) return sum;
      const r = e.local_rate && e.local_rate !== '' ? parseFloat(e.local_rate) : (e.rate || 0);
      const bh = typeof e.local_billable_hours === 'string' && e.local_billable_hours.includes(':')
        ? hhmmToDecimal(e.local_billable_hours)
        : e.billable_hours;
      return sum + bh * r;
    }, 0);

    if (currentTotal <= 0) return;

    // Scale all billable hours proportionally to reach target
    const scaleFactor = target / currentTotal;

    setEntries((prev) =>
      prev.map((e) => {
        if (!e.billable) return e;
        const currentBh = typeof e.local_billable_hours === 'string' && e.local_billable_hours.includes(':')
          ? hhmmToDecimal(e.local_billable_hours)
          : e.billable_hours;
        const newBh = Math.max(0, parseFloat((currentBh * scaleFactor).toFixed(3)));
        return {
          ...e,
          billable_hours: newBh,
          local_billable_hours: decimalToHHMM(newBh),
          dirty: true,
        };
      })
    );
  }, [targetAmount, entries]);

  const applyTargetSlider = useCallback(
    (pct: number) => {
      const scaleFactor = pct / 100;

      setEntries((prev) =>
        prev.map((e) => {
          if (!e.billable) return e;
          const orig = originalEntries.find((o) => o.id === e.id);
          if (!orig) return e;
          const origBh = orig.billable_hours;
          const newBh = Math.max(0, parseFloat((origBh * scaleFactor).toFixed(3)));
          return {
            ...e,
            billable_hours: newBh,
            local_billable_hours: decimalToHHMM(newBh),
            dirty: true,
          };
        })
      );
    },
    [originalEntries]
  );

  // ─── Per-User Slider / Target Logic ───────────────────────────────────

  const applyUserSlider = useCallback(
    (userId: number, pct: number) => {
      const scaleFactor = pct / 100;
      setEntries((prev) =>
        prev.map((e) => {
          if (e.user !== userId || !e.billable) return e;
          const orig = originalEntries.find((o) => o.id === e.id);
          if (!orig) return e;
          const origBh = orig.billable_hours;
          const newBh = Math.max(0, parseFloat((origBh * scaleFactor).toFixed(3)));
          return {
            ...e,
            billable_hours: newBh,
            local_billable_hours: decimalToHHMM(newBh),
            dirty: true,
          };
        })
      );
    },
    [originalEntries]
  );

  const applyUserTargetAmount = useCallback(
    (userId: number) => {
      const target = parseFloat(userTargetAmounts[userId]);
      if (isNaN(target) || target <= 0) return;

      // Compute current fee for this user
      const userEntries = entries.filter((e) => e.user === userId && e.billable);
      const currentFee = userEntries.reduce((sum, e) => {
        const bh = typeof e.local_billable_hours === 'string' && e.local_billable_hours.includes(':')
          ? hhmmToDecimal(e.local_billable_hours)
          : e.billable_hours;
        const r = e.local_rate && e.local_rate !== '' ? parseFloat(e.local_rate) : (e.rate || 0);
        return sum + bh * r;
      }, 0);

      if (currentFee <= 0) return;
      const scaleFactor = target / currentFee;

      setEntries((prev) =>
        prev.map((e) => {
          if (e.user !== userId || !e.billable) return e;
          const currentBh = typeof e.local_billable_hours === 'string' && e.local_billable_hours.includes(':')
            ? hhmmToDecimal(e.local_billable_hours)
            : e.billable_hours;
          const newBh = Math.max(0, parseFloat((currentBh * scaleFactor).toFixed(3)));
          return {
            ...e,
            billable_hours: newBh,
            local_billable_hours: decimalToHHMM(newBh),
            dirty: true,
          };
        })
      );

      // Update the slider to reflect the new percentage relative to original
      const origData = originalUserFees.get(userId);
      if (origData && origData.fee > 0) {
        setUserSliderValues((prev) => ({
          ...prev,
          [userId]: Math.round((target / origData.fee) * 100),
        }));
      }

      toast({ title: 'Target Applied', description: `${userMap.get(userId)}'s hours scaled to reach ${formatCurrency(target)}` });
    },
    [userTargetAmounts, entries, originalUserFees, userMap]
  );

  // Preview fee from slider — pure math, no entry mutation
  const sliderPreviewFee = useMemo(() => {
    if (targetSliderValue === 100) return null; // no preview needed
    return originalTotalFee * (targetSliderValue / 100);
  }, [targetSliderValue, originalTotalFee]);

  // The displayed total fee: use slider preview during drag, else computed
  const displayTotalFee = sliderPreviewFee !== null ? sliderPreviewFee : totalFee;
  const displayBillableHours = targetSliderValue !== 100
    ? (serverSum?.billable_hours ?? totalBillableHours) * (targetSliderValue / 100)
    : totalBillableHours;

  // ─── Adjusted total (accounting for discounts / flat fees / deductions) ───

  const adjustmentBreakdown = useMemo(() => {
    const base = displayTotalFee;
    let discountValue = 0;
    if (adjustments.discount_type === 'percentage') {
      const pct = parseFloat(adjustments.discount_percentage) || 0;
      discountValue = base * (pct / 100);
    } else if (adjustments.discount_type === 'fixed') {
      discountValue = parseFloat(adjustments.discount_amount) || 0;
    } else if (adjustments.discount_type === 'both') {
      const pct = parseFloat(adjustments.discount_percentage) || 0;
      const fixed = parseFloat(adjustments.discount_amount) || 0;
      discountValue = base * (pct / 100) + fixed;
    }
    const flatFee = parseFloat(adjustments.flat_fee_adjustment) || 0;
    const deduction = parseFloat(adjustments.fixed_deduction_amount) || 0;
    const hourAdj = parseFloat(adjustments.hour_adjustment) || 0;
    const rateAdj = parseFloat(adjustments.rate_adjustment) || 0;
    const hourAdjValue = hourAdj !== 0 ? hourAdj * (rateAdj || 0) : 0;
    const targetAmt = parseFloat(adjustments.target_amount) || 0;
    // Sum all extra adjustment lines
    const adjLinesTotal = adjustments.adjustment_lines.reduce((sum, line) => {
      return sum + (parseFloat(String(line.amount)) || 0);
    }, 0);
    let adjustedTotal = base - discountValue + flatFee - deduction + hourAdjValue + adjLinesTotal;
    if (targetAmt > 0) adjustedTotal = targetAmt;
    const hasAnyAdjustment = discountValue !== 0 || flatFee !== 0 || deduction !== 0 || hourAdjValue !== 0 || targetAmt > 0 || adjustments.detail_level !== '' || adjustments.adjustment_lines.length > 0;
    return { base, discountValue, flatFee, deduction, hourAdjValue, targetAmt, adjLinesTotal, adjustedTotal, hasAnyAdjustment };
  }, [displayTotalFee, adjustments]);

  // ─── Per-member rate override ─────────────────────────────────────────────

  const applyMemberRate = useCallback(
    (userId: number) => {
      const newRate = parseFloat(memberRateOverrides[userId]);
      if (isNaN(newRate) || newRate < 0) return;

      setEntries((prev) =>
        prev.map((e) => {
          if (e.user !== userId) return e;
          return { ...e, rate: newRate, local_rate: formatRate(newRate), dirty: true };
        })
      );

      toast({ title: 'Rate Updated', description: `${userMap.get(userId)}'s rate set to €${formatRate(newRate)}` });
    },
    [memberRateOverrides, userMap]
  );

  // ─── Save all changes ────────────────────────────────────────────────────

  const saveAllChanges = useCallback(async () => {
    const dirtyEntries = entries.filter((e) => e.dirty);
    if (dirtyEntries.length === 0) {
      toast({ title: 'Info', description: 'No changes to save' });
      return;
    }

    setIsSaving(true);
    let successCount = 0;
    let failCount = 0;

    try {
      // Build batch of updates — include all editable fields
      const updates = dirtyEntries.map((e) => {
        const bh = typeof e.local_billable_hours === 'string' && e.local_billable_hours.includes(':')
          ? hhmmToDecimal(e.local_billable_hours)
          : e.billable_hours;
        const hrs = typeof e.local_hours === 'string' && e.local_hours.includes(':')
          ? hhmmToDecimal(e.local_hours)
          : e.hours;
        const rate = e.local_rate && e.local_rate !== '' ? parseFloat(e.local_rate) : e.rate;
        return {
          id: e.id,
          date: e.date,
          user: e.user,
          matter: e.matter,
          description: e.local_description ?? e.description,
          hours: isNaN(hrs) ? 0 : hrs,
          billable: e.billable,
          billable_hours: isNaN(bh) ? 0 : bh,
          rate: rate,
        };
      });

      // Send individual PATCH requests (batched with Promise.allSettled for efficiency)
      const batchSize = 10;
      for (let i = 0; i < updates.length; i += batchSize) {
        const batch = updates.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map((u) => {
            const { id, ...payload } = u;
            return api.patch(`${ROUTES.API.TIMESHEET}${id}/`, payload);
          })
        );
        results.forEach((r) => {
          if (r.status === 'fulfilled') successCount++;
          else failCount++;
        });
      }

      // Refresh from server
      await fetchEntries();

      if (failCount > 0) {
        toast({
          title: 'Partial Save',
          description: `${successCount} updated, ${failCount} failed`,
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Saved', description: `${successCount} entries updated successfully` });
      }
    } catch (error) {
      showErrorToast(error, 'Error', 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }, [entries, fetchEntries]);

  // ─── Matter transfer ──────────────────────────────────────────────────────

  const handleTransferMatter = useCallback(async () => {
    if (!transferMatterId || selectedIds.size === 0) return;

    setIsSaving(true);
    try {
      await api.post(`${ROUTES.API.TIMESHEET}bulk_update/`, {
        ids: Array.from(selectedIds),
        matter: transferMatterId,
      });
      setSelectedIds(new Set());
      setTransferMatterOpen(false);
      setTransferMatterId(null);
      await fetchEntries();
      toast({ title: 'Success', description: `${selectedIds.size} entries moved to ${matterMap.get(transferMatterId) || 'new matter'}` });
    } catch (error) {
      showErrorToast(error, 'Error', 'Failed to transfer entries');
    } finally {
      setIsSaving(false);
    }
  }, [transferMatterId, selectedIds, fetchEntries, matterMap]);

  // ─── Selection ────────────────────────────────────────────────────────────

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === entries.length) return new Set();
      return new Set(entries.map((e) => e.id));
    });
  }, [entries]);

  // ─── Reset ────────────────────────────────────────────────────────────────

  const resetAllChanges = useCallback(() => {
    setEntries(
      originalEntries.map((e) => ({
        ...e,
        local_billable_hours: decimalToHHMM(e.billable_hours),
        local_hours: decimalToHHMM(e.hours),
        local_rate: e.rate !== null ? formatRate(e.rate) : '',
        local_description: e.description,
        local_date: formatDateDisplay(e.date),
        dirty: false,
      }))
    );
    setTargetAmount('');
    setTargetSliderValue(100);
    setMemberRateOverrides({});
    setUserSliderValues({});
    setUserTargetAmounts({});
    toast({ title: 'Reset', description: 'All changes reverted to server values' });
  }, [originalEntries]);

  // ─── Access check ─────────────────────────────────────────────────────────

  if (!isUserAdmin) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">You do not have permission to access this page.</p>
        </div>
      </Layout>
    );
  }

  const hasChanges = entries.some((e) => e.dirty);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" asChild>
              <Link to={ROUTES.CREATE_INVOICE}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Invoice Creation
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Timesheet Review</h1>
              <p className="text-sm text-muted-foreground">
                {matterName ? `${matterName} — ${entries.length} uninvoiced entries` : 'Select a matter to review'}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={resetAllChanges} disabled={!hasChanges || isSaving}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
            <Button size="sm" onClick={saveAllChanges} disabled={!hasChanges || isSaving}>
              <Save className="h-4 w-4 mr-1" />
              Save {hasChanges ? `(${entries.filter((e) => e.dirty).length})` : ''}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <Filter className="h-3 w-3" /> Date From
                </Label>
                <DatePicker
                  value={filterDateFrom}
                  onChange={setFilterDateFrom}
                  className="h-8 w-36 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date To</Label>
                <DatePicker
                  value={filterDateTo}
                  onChange={setFilterDateTo}
                  className="h-8 w-36 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">User</Label>
                <UserCombobox
                  value={filterUserIds.length === 1 ? filterUserIds[0] : null}
                  onChange={(id) => setFilterUserIds(id ? [id] : [])}
                  users={users}
                  noneLabel="All users"
                  triggerClassName="h-8 w-40 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Matter</Label>
                <ComboboxSelect
                  options={sortedMatterOptions}
                  value={filterMatterIds.length === 1 ? filterMatterIds[0].toString() : undefined}
                  onValueChange={(v) => setFilterMatterIds(v ? [Number(v)] : [])}
                  placeholder="Select matter..."
                  searchPlaceholder="Filter matters..."
                  emptyMessage="No matters found."
                  triggerClassName="h-8 text-xs"
                  popoverClassName="w-[500px]"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Description..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    className="h-8 w-40 text-xs pl-7"
                  />
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  setFilterDateFrom('');
                  setFilterDateTo('');
                  setFilterUserIds([]);
                  setFilterMatterIds(matterId ? [matterId] : []);
                  setFilterSearch('');
                }}
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => fetchEntries()}
                disabled={isLoading}
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* No-matter prompt */}
        {!activeMatterId && !isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <Filter className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-1">Select a matter</h3>
              <p className="text-sm text-muted-foreground">
                Choose a matter from the filter above to load its uninvoiced timesheets.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Summary Table */}
        {activeMatterId && <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Billable Hours</TableHead>
                  <TableHead className="font-semibold">Current Total Fee</TableHead>
                  <TableHead className="font-semibold">Avg Rate</TableHead>
                  {adjustmentBreakdown.hasAnyAdjustment && (
                    <TableHead className="font-semibold">Adjusted Total</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">{decimalToHHMM(displayBillableHours)}</TableCell>
                  <TableCell>
                    <span className={cn("font-medium", displayTotalFee !== originalTotalFee && "text-orange-600")}>
                      {formatCurrency(displayTotalFee)}
                    </span>
                    {displayTotalFee !== originalTotalFee && (
                      <span className="text-xs text-muted-foreground ml-1">
                        (was {formatCurrency(originalTotalFee)})
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{serverSum ? formatCurrency(serverSum.avg_rate) : '—'}</TableCell>
                  {adjustmentBreakdown.hasAnyAdjustment && (
                    <TableCell>
                      <span className="font-semibold text-primary">
                        {formatCurrency(adjustmentBreakdown.adjustedTotal)}
                      </span>
                      <div className="text-[10px] text-muted-foreground mt-0.5 space-y-px">
                        {adjustmentBreakdown.discountValue !== 0 && (
                          <div>Discount: −{formatCurrency(adjustmentBreakdown.discountValue)}</div>
                        )}
                        {adjustmentBreakdown.flatFee !== 0 && (
                          <div>Flat fee: {adjustmentBreakdown.flatFee > 0 ? '+' : ''}{formatCurrency(adjustmentBreakdown.flatFee)}</div>
                        )}
                        {adjustmentBreakdown.deduction !== 0 && (
                          <div>Deduction: −{formatCurrency(adjustmentBreakdown.deduction)}</div>
                        )}
                        {adjustmentBreakdown.adjLinesTotal !== 0 && (
                          <div>Adj. lines: {adjustmentBreakdown.adjLinesTotal > 0 ? '+' : ''}{formatCurrency(adjustmentBreakdown.adjLinesTotal)}</div>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>}

        {activeMatterId && <>
        {/* Target Amount Tool */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4" />
              Target Amount
            </CardTitle>
            <CardDescription>
              Set a target fee and the system will proportionally adjust billable hours to reach it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-xs">Target Fee (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="100"
                  placeholder="e.g. 10000"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  style={{ MozAppearance: 'textfield' }}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">
                  Or scale hours: {targetSliderValue}% of original
                  {sliderPreviewFee !== null && (
                    <span className="ml-2 text-orange-600 font-medium">
                      → {formatCurrency(sliderPreviewFee)}
                    </span>
                  )}
                </Label>
                <Slider
                  min={0}
                  max={150}
                  step={1}
                  value={targetSliderValue}
                  onChange={(v) => setTargetSliderValue(v)}
                  onCommit={(v) => {
                    setTargetSliderValue(v);
                    applyTargetSlider(v);
                  }}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>0%</span>
                  <span>100%</span>
                  <span>150%</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={applyTargetAmount}
                  disabled={!targetAmount || parseFloat(targetAmount) <= 0}
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Apply Target
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetAllChanges}
                  disabled={!hasChanges}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              </div>
            </div>

            {/* Live preview of what target would do */}
            {targetAmount && parseFloat(targetAmount) > 0 && (
              <div className="bg-muted/50 rounded-md p-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span className="font-medium">Preview</span>
                </div>
                {(() => {
                  const target = parseFloat(targetAmount);
                  const diff = target - displayTotalFee;
                  const pct = displayTotalFee > 0 ? ((target / displayTotalFee) * 100).toFixed(1) : '0';
                  if (Math.abs(diff) < 1) {
                    return <p className="text-green-600">Current total matches target.</p>;
                  }
                  return (
                    <p>
                      {diff > 0 ? 'Increase' : 'Decrease'} of{' '}
                      <span className="font-semibold">{formatCurrency(Math.abs(diff))}</span>{' '}
                      needed ({pct}% of current). Click "Apply Target" to proportionally adjust all billable hours.
                    </p>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Member Summary */}
        <Collapsible open={memberSummaryOpen} onOpenChange={setMemberSummaryOpen}>
          <Card>
            <CardHeader className="pb-3">
              <CollapsibleTrigger asChild>
                <button className="flex items-center justify-between w-full text-left">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Team Member Summary
                  </CardTitle>
                  {memberSummaryOpen ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="space-y-3 pt-0">
                {userSummaries.map((s) => {
                  const origData = originalUserFees.get(s.userId);
                  const origFee = origData?.fee || 0;
                  const sliderValue = userSliderValues[s.userId] ?? 100;
                  const sliderPreview = sliderValue !== 100 && origFee > 0 ? origFee * (sliderValue / 100) : null;

                  return (
                    <div key={s.userId} className="border rounded-lg p-3 space-y-2">
                      {/* Summary row */}
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-sm">{s.userName}</span>
                          <Badge variant="secondary" className="text-[10px]">{s.entryCount} entries</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-muted-foreground">{decimalToHHMM(s.totalBillableHours)} hrs</span>
                          <span className={cn('font-medium', s.totalFee !== origFee && 'text-orange-600')}>
                            {formatCurrency(s.totalFee)}
                            {s.totalFee !== origFee && (
                              <span className="text-xs text-muted-foreground ml-1">(was {formatCurrency(origFee)})</span>
                            )}
                          </span>
                          <span className="text-muted-foreground">@ {formatCurrency(s.avgRate)}/hr</span>
                        </div>
                      </div>

                      {/* Controls row */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end pt-2 border-t">
                        {/* Per-user Slider */}
                        <div className="space-y-1">
                          <Label className="text-xs">
                            Scale hours: {sliderValue}%
                            {sliderPreview !== null && (
                              <span className="ml-2 text-orange-600 font-medium">→ {formatCurrency(sliderPreview)}</span>
                            )}
                          </Label>
                          <Slider
                            min={0}
                            max={150}
                            step={1}
                            value={sliderValue}
                            onChange={(v) => setUserSliderValues((prev) => ({ ...prev, [s.userId]: v }))}
                            onCommit={(v) => {
                              setUserSliderValues((prev) => ({ ...prev, [s.userId]: v }));
                              applyUserSlider(s.userId, v);
                            }}
                          />
                          <div className="flex justify-between text-[10px] text-muted-foreground">
                            <span>0%</span>
                            <span>100%</span>
                            <span>150%</span>
                          </div>
                        </div>

                        {/* Per-user Target Fee */}
                        <div className="space-y-1">
                          <Label className="text-xs">Target Fee (€)</Label>
                          <div className="flex gap-1">
                            <Input
                              type="number"
                              min="0"
                              step="100"
                              placeholder={`e.g. ${Math.round(origFee / 100) * 100 || 5000}`}
                              value={userTargetAmounts[s.userId] ?? ''}
                              onChange={(e) =>
                                setUserTargetAmounts((prev) => ({ ...prev, [s.userId]: e.target.value }))
                              }
                              className="h-7 text-xs flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              style={{ MozAppearance: 'textfield' }}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs px-2"
                              onClick={() => applyUserTargetAmount(s.userId)}
                              disabled={!userTargetAmounts[s.userId] || parseFloat(userTargetAmounts[s.userId]) <= 0}
                            >
                              <Sparkles className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>

                        {/* Rate Override */}
                        <div className="space-y-1">
                          <Label className="text-xs">Override Rate (€/hr)</Label>
                          <div className="flex gap-1">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="New rate"
                              value={memberRateOverrides[s.userId] ?? ''}
                              onChange={(e) =>
                                setMemberRateOverrides((prev) => ({
                                  ...prev,
                                  [s.userId]: e.target.value,
                                }))
                              }
                              className="h-7 text-xs flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              style={{ MozAppearance: 'textfield' }}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs px-2"
                              onClick={() => applyMemberRate(s.userId)}
                              disabled={!memberRateOverrides[s.userId]}
                            >
                              Apply
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Invoice Adjustments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Invoice Adjustments
              {adjustmentBreakdown.hasAnyAdjustment && (
                <Badge variant="secondary" className="text-[10px]">Active</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Discounts, flat fees, and deductions that will be applied when creating the invoice for this matter.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Discount Section */}
            <Accordion type="single" collapsible defaultValue={adjustments.discount_type !== 'none' ? 'discount' : undefined}>
              <AccordionItem value="discount">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Percent className="h-4 w-4" />
                    Discount
                    {adjustments.discount_type !== 'none' && <Badge variant="secondary" className="text-[10px]">Active</Badge>}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label className="text-xs">Discount Type</Label>
                    <Select
                      value={adjustments.discount_type}
                      onValueChange={(v) => updateAdjustment('discount_type', v as InvoiceAdjustments['discount_type'])}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DISCOUNT_TYPES.map((dt) => (
                          <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {adjustments.discount_type === 'percentage' && (
                    <div className="space-y-2">
                      <Label className="text-xs">Discount Percentage (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="e.g. 10"
                        value={adjustments.discount_percentage}
                        onChange={(e) => updateAdjustment('discount_percentage', e.target.value)}
                      />
                      {adjustments.discount_percentage && (
                        <p className="text-xs text-muted-foreground">
                          = {formatCurrency(displayTotalFee * (parseFloat(adjustments.discount_percentage) || 0) / 100)} off {formatCurrency(displayTotalFee)}
                        </p>
                      )}
                    </div>
                  )}

                  {adjustments.discount_type === 'fixed' && (
                    <div className="space-y-2">
                      <Label className="text-xs">Discount Amount (€)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="e.g. 500.00"
                        value={adjustments.discount_amount}
                        onChange={(e) => updateAdjustment('discount_amount', e.target.value)}
                      />
                    </div>
                  )}

                  {adjustments.discount_type === 'both' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Discount Percentage (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="e.g. 10"
                          value={adjustments.discount_percentage}
                          onChange={(e) => updateAdjustment('discount_percentage', e.target.value)}
                        />
                        {adjustments.discount_percentage && (
                          <p className="text-xs text-muted-foreground">
                            = {formatCurrency(displayTotalFee * (parseFloat(adjustments.discount_percentage) || 0) / 100)}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Fixed Amount (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="e.g. 500.00"
                          value={adjustments.discount_amount}
                          onChange={(e) => updateAdjustment('discount_amount', e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {adjustments.discount_type !== 'none' && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-xs">Description (appears on invoice)</Label>
                        <Input
                          placeholder="e.g. Loyalty discount"
                          value={adjustments.discount_description}
                          onChange={(e) => updateAdjustment('discount_description', e.target.value)}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={adjustments.show_discount_on_invoice}
                          onCheckedChange={(v) => updateAdjustment('show_discount_on_invoice', v)}
                        />
                        <Label className="text-xs">Show discount line on invoice</Label>
                      </div>
                    </>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Flat Fee Adjustment */}
            <Accordion type="single" collapsible defaultValue={adjustments.flat_fee_adjustment && adjustments.flat_fee_adjustment !== '0' ? 'flatfee' : undefined}>
              <AccordionItem value="flatfee">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Flat Fee Adjustment
                    {adjustments.flat_fee_adjustment && adjustments.flat_fee_adjustment !== '0' && (
                      <Badge variant="secondary" className="text-[10px]">Active</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label className="text-xs">Amount (€) — positive adds, negative subtracts</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g. 1500.00"
                      value={adjustments.flat_fee_adjustment}
                      onChange={(e) => updateAdjustment('flat_fee_adjustment', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Description</Label>
                    <Input
                      placeholder="e.g. Advisory retainer"
                      value={adjustments.flat_fee_description}
                      onChange={(e) => updateAdjustment('flat_fee_description', e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={adjustments.show_flat_fee_on_invoice}
                      onCheckedChange={(v) => updateAdjustment('show_flat_fee_on_invoice', v)}
                    />
                    <Label className="text-xs">Show flat fee line on invoice</Label>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Fixed Deduction */}
            <Accordion type="single" collapsible defaultValue={adjustments.fixed_deduction_amount && adjustments.fixed_deduction_amount !== '0' ? 'deduction' : undefined}>
              <AccordionItem value="deduction">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Minus className="h-4 w-4" />
                    Fixed Deduction
                    {adjustments.fixed_deduction_amount && adjustments.fixed_deduction_amount !== '0' && (
                      <Badge variant="secondary" className="text-[10px]">Active</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <p className="text-xs text-muted-foreground">
                    Deduct a fixed amount — e.g. a pre-paid retainer or client contribution.
                  </p>
                  <div className="space-y-2">
                    <Label className="text-xs">Deduction Amount (€)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="e.g. 2000.00"
                      value={adjustments.fixed_deduction_amount}
                      onChange={(e) => updateAdjustment('fixed_deduction_amount', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Description</Label>
                    <Input
                      placeholder="e.g. Pre-paid client retainer"
                      value={adjustments.fixed_deduction_description}
                      onChange={(e) => updateAdjustment('fixed_deduction_description', e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={adjustments.show_fixed_deduction_on_invoice}
                      onCheckedChange={(v) => updateAdjustment('show_fixed_deduction_on_invoice', v)}
                    />
                    <Label className="text-xs">Show deduction on invoice</Label>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Hour & Rate Adjustment */}
            <Accordion type="single" collapsible defaultValue={(adjustments.hour_adjustment || adjustments.rate_adjustment) ? 'hours' : undefined}>
              <AccordionItem value="hours">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Minus className="h-4 w-4" />
                    Hour &amp; Rate Adjustment
                    {adjustments.hour_adjustment && adjustments.hour_adjustment !== '0' && (
                      <Badge variant="secondary" className="text-[10px]">Active</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <p className="text-xs text-muted-foreground">
                    Write off hours or override the blended rate used on the invoice line. Use the Target Amount tool above for per-timesheet scaling instead.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Hour Adjustment</Label>
                      <Input
                        type="number"
                        step="0.25"
                        placeholder="e.g. -2.5 to write off hours"
                        value={adjustments.hour_adjustment}
                        onChange={(e) => updateAdjustment('hour_adjustment', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Rate Override (€/hr)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Leave empty for weighted avg"
                        value={adjustments.rate_adjustment}
                        onChange={(e) => updateAdjustment('rate_adjustment', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Description</Label>
                    <Input
                      placeholder="e.g. Hours written off per partner review"
                      value={adjustments.hour_adjustment_description}
                      onChange={(e) => updateAdjustment('hour_adjustment_description', e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={adjustments.show_hour_adjustment_on_invoice}
                      onCheckedChange={(v) => updateAdjustment('show_hour_adjustment_on_invoice', v)}
                    />
                    <Label className="text-xs">Show adjustment on invoice</Label>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Target Amount (back-solve) */}
            <Accordion type="single" collapsible defaultValue={adjustments.target_amount && adjustments.target_amount !== '0' ? 'invoicetarget' : undefined}>
              <AccordionItem value="invoicetarget">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Invoice Target Amount
                    {adjustments.target_amount && adjustments.target_amount !== '0' && (
                      <Badge variant="secondary" className="text-[10px]">Active</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <p className="text-xs text-muted-foreground">
                    Set a desired invoice total. The system will automatically calculate a flat-fee adjustment to reach this amount (before VAT).
                  </p>
                  <div className="space-y-2">
                    <Label className="text-xs">Desired Invoice Total (€ excl. VAT)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="e.g. 10000.00"
                      value={adjustments.target_amount}
                      onChange={(e) => updateAdjustment('target_amount', e.target.value)}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Additional Adjustment Lines */}
            <div className="border-t pt-3">
              <AdjustmentLinesEditor
                lines={adjustments.adjustment_lines}
                onChange={(lines) => updateAdjustment('adjustment_lines', lines)}
              />
              {adjustments.adjustment_lines.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Lines total: {formatCurrency(
                    adjustments.adjustment_lines.reduce((sum, l) => sum + (parseFloat(String(l.amount)) || 0), 0)
                  )}
                </p>
              )}
            </div>

            {/* Invoice Detail Level */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Invoice Detail Level</Label>
              <p className="text-xs text-muted-foreground">
                Override for this invoice. Leave as default to use the matter or firm setting.
              </p>
              <Select
                value={adjustments.detail_level || 'inherit'}
                onValueChange={(v) => updateAdjustment('detail_level', (v === 'inherit' ? '' : v) as InvoiceAdjustments['detail_level'])}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Use default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">Use default</SelectItem>
                  {INVOICE_DETAIL_LEVELS.map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label} — {l.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Summary + Reset */}
            <div className="flex items-center justify-between">
              {adjustmentBreakdown.hasAnyAdjustment ? (
                <div className="text-sm">
                  <span className="text-muted-foreground">Fee after adjustments: </span>
                  <span className="font-semibold text-primary">{formatCurrency(adjustmentBreakdown.adjustedTotal)}</span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No adjustments set.</p>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAdjustments({ ...DEFAULT_ADJUSTMENTS })}
                disabled={!adjustmentBreakdown.hasAnyAdjustment}
              >
                Reset Adjustments
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
            <span className="text-sm font-medium">
              {selectedIds.size} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setTransferMatterOpen(true)}
            >
              <ArrowRightLeft className="h-4 w-4 mr-1" />
              Move to Matter
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const params = new URLSearchParams();
                  selectedIds.forEach((id) => params.append('ids', id.toString()));
                  await api.post(`${ROUTES.API.TIMESHEET_APPROVE}?${params.toString()}`);
                  await fetchEntries();
                  toast({ title: 'Approved', description: `${selectedIds.size} entries approved` });
                  setSelectedIds(new Set());
                } catch (error) {
                  showErrorToast(error, 'Error', 'Failed to approve entries');
                }
              }}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setWriteOffOpen(true)}
            >
              Write off
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </div>
        )}

        <WriteOffDialog
          open={writeOffOpen}
          onOpenChange={setWriteOffOpen}
          timesheetIds={Array.from(selectedIds)}
          onDone={() => {
            setSelectedIds(new Set());
            fetchEntries();
          }}
        />

        {/* Timesheet Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Timesheet Entries</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No uninvoiced timesheets found{matterId ? ` for this matter` : ''}.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="table-fixed w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 px-2">
                        <Checkbox
                          checked={selectedIds.size === entries.length && entries.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="w-28 px-2 font-semibold">Date</TableHead>
                      <TableHead className="w-32 px-2 font-semibold">Matter</TableHead>
                      <TableHead className="w-28 px-2 font-semibold">User</TableHead>
                      <TableHead className="w-48 px-2 font-semibold">Description</TableHead>
                      <TableHead className="w-20 px-2 font-semibold text-center">Bill. Hours</TableHead>
                      <TableHead className="w-20 px-2 font-semibold text-center">Rate</TableHead>
                      <TableHead className="w-20 px-2 font-semibold text-right">Fee</TableHead>
                      <TableHead className="w-12 px-2"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TimesheetRow
                        key={entry.id}
                        entry={entry}
                        selected={selectedIds.has(entry.id)}
                        matterLabel={matterMap.get(entry.matter) || ''}
                        userLabel={userMap.get(entry.user) || ''}
                        matters={matters}
                        users={users}
                        onToggleSelect={toggleSelect}
                        onUpdateEntry={updateEntry}
                        onUpdateBillableHours={updateBillableHoursDisplay}
                        onFormatBillableHoursBlur={formatBillableHoursOnBlur}
                        onUpdateRate={updateRateDisplay}
                        onFormatRateBlur={formatRateOnBlur}
                        onUpdateDate={handleDateChange}
                        onCommitDate={handleDateCommit}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        </>}
      </div>

      {/* Transfer Matter Dialog */}
      <Dialog open={transferMatterOpen} onOpenChange={setTransferMatterOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Move Entries to Another Matter</DialogTitle>
            <DialogDescription>
              {selectedIds.size} selected entries will be moved to the chosen matter.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Target Matter</Label>
              <MatterCombobox
                value={transferMatterId}
                onChange={setTransferMatterId}
                matters={matters.filter((m) => m.id !== activeMatterId)}
                includeInactive
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferMatterOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleTransferMatter} disabled={!transferMatterId || isSaving}>
              {isSaving ? 'Moving...' : `Move ${selectedIds.size} Entries`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
